// Reading and writing date property cells — see PAR-27.
//
// Timed values are stored as NAIVE LOCAL datetimes ("2026-08-12T09:30"), not
// UTC instants. Two reasons, both load-bearing:
//
//   1. `date-filter.ts` regex-extracts the ISO day straight out of the stored
//      string. A UTC instant shifts that day across midnight — 00:30 IST
//      serialises to "2026-08-11T19:00:00.000Z" and the row filters into the
//      wrong day.
//   2. JS parses a date-only string as UTC midnight but a date-TIME string
//      with no offset as local, so day-only cells keep their exact existing
//      behaviour while timed cells read back at the clock time they were set.
//
// The mention node (`mentionDateCalender`) stores `toISOString()` instead. That
// is fine there — it is only ever read back through `new Date()`. Do not copy
// it here.
//
// A timed value carries the zone it was typed in ("2026-09-17T09:30[Asia/Kolkata]");
// values without one — day-only, or written before this — never convert.

import { convertWallClock, isValidTimeZone, wallClockInstant } from "@/lib/timezone";

const DAY = "\\d{4}-\\d{2}-\\d{2}";
const TIME = "\\d{2}:\\d{2}";
/** An ISO day, optionally followed by a time. */
const DATE_PATTERN = new RegExp(`${DAY}(?:T${TIME})?`, "g");
/** A pre-PAR-27 zoned instant, e.g. "2026-03-10T14:30:00.000Z". */
const ZONED_PATTERN = new RegExp(`(${DAY})T${TIME}(?::\\d{2})?(?:\\.\\d+)?(?:Z|[+-]\\d{2}:?\\d{2})`, "g");
/** RFC 9557 zone suffix, e.g. "[Asia/Kolkata]". A name, not an offset — ZONED_PATTERN would swallow an offset. */
const CELL_ZONE_PATTERN = /\[([A-Za-z0-9_+\-/]+)\]/;

export type TimeFormat = "12h" | "24h";

export interface DateCell {
  start: Date;
  /** Null for a single date. */
  end: Date | null;
  /** True when EITHER endpoint carried a time. Most callers want this. */
  hasTime: boolean;
  /** Per-endpoint, because a range may carry a time at one end only. */
  startHasTime: boolean;
  endHasTime: boolean;
  /** Zone the clock was typed in. Absent on day-only and pre-zone values, which never convert. */
  zone?: string;
}

/** Parses "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm" in local time. */
function parseToken(token: string): Date | null {
  // Strip the zone first: "09:30[Asia/Kolkata]".split(":") gives NaN minutes.
  const [day, time] = token.replace(CELL_ZONE_PATTERN, "").split("T");
  if (!day) return null;
  const [y, m, d] = day.split("-").map(Number);
  if (!y || !m || !d) return null;
  const [h, min] = time ? time.split(":").map(Number) : [0, 0];
  const date = new Date(y, m - 1, d, h ?? 0, min ?? 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Local midnight of a token's day. Use this instead of splitting on "-":
 * `"2026-08-12T09:30".split("-")` yields NaN for the day and serialises back
 * as "NaN-NaN-NaN".
 */
export function parseDayStart(token: unknown): Date | null {
  const parsed = parseToken(String(token ?? "").trim());
  if (!parsed) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/**
 * Reduces a stored cell to its start, end and time-ness.
 *
 * Tolerant by necessity — the same property has been written in several shapes
 * over time:
 *   "2026-03-10"                              single day
 *   "2026-03-10,2026-03-15"                   range
 *   "2026-08-12T09:30"                        timed (PAR-27)
 *   "2025-10-22 [2025-10-22 - 2025-10-25]"    legacy due-date form
 *   "2026-03-10T14:30:00.000Z"                legacy ISO instant
 * Taking the first and last match handles all of them.
 *
 * Zoned instants collapse to their day. They predate PAR-27 and have only ever
 * rendered day-granular, so surfacing a time on them now would put clock times
 * on existing rows that never had one.
 */
export function parseDateCell(value: unknown): DateCell | null {
  if (value === null || value === undefined) return null;
  const matches = String(value).replace(ZONED_PATTERN, "$1").match(DATE_PATTERN);
  const first = matches?.[0];
  const last = matches?.[matches.length - 1];
  if (!first || !last) return null;

  const start = parseToken(first);
  if (!start) return null;

  const end = matches.length > 1 ? parseToken(last) : null;
  const firstHasTime = first.includes("T");
  const lastHasTime = !!end && last.includes("T");
  const hasTime = firstHasTime || lastHasTime;

  // The legacy "[2025-10-22 - 2025-10-25]" form has spaces, so the pattern skips it.
  const zoneMatch = hasTime ? CELL_ZONE_PATTERN.exec(String(value)) : null;
  const zone = zoneMatch?.[1] && isValidTimeZone(zoneMatch[1]) ? zoneMatch[1] : undefined;

  // Reversing the span has to carry each endpoint's own time-ness with it.
  if (end && end < start) {
    return { start: end, end: start, hasTime, startHasTime: lastHasTime, endHasTime: firstHasTime, zone };
  }
  return { start, end, hasTime, startHasTime: firstHasTime, endHasTime: lastHasTime, zone };
}

/** True when the stored cell carries a time. */
export function cellHasTime(value: unknown): boolean {
  return parseDateCell(value)?.hasTime ?? false;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** Formats a Date as "YYYY-MM-DD", or "YYYY-MM-DDTHH:mm" when timed. */
function serializeToken(date: Date, includeTime: boolean): string {
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  if (!includeTime) return day;
  return `${day}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** "YYYY-MM-DD" from a Date's LOCAL parts — `toISOString().slice(0,10)` gives the UTC day. */
export function formatDay(date: Date): string {
  return serializeToken(date, false);
}

/** Day key for a stored cell, tolerating every stored shape. Null when unparseable. */
export function dateCellDayKey(value: unknown): string | null {
  const cell = parseDateCell(value);
  return cell ? formatDay(cell.start) : null;
}

/**
 * Writes a cell value. Returns "" for an empty selection, which is how the
 * inputs signal "cleared".
 */
export function serializeDateCell(
  start: Date | null | undefined,
  end?: Date | null,
  /**
   * One flag for both ends, or one per end. A range may carry a time at only
   * one end, and forcing the other to "T00:00" invents a midnight the user
   * never typed.
   */
  includeTime: boolean | { start: boolean; end: boolean } = false,
  /** IANA zone the clock was typed in. Recorded so the value converts for other viewers. */
  zone?: string,
): string {
  if (!start || Number.isNaN(start.getTime())) return "";
  const startTime = typeof includeTime === "boolean" ? includeTime : includeTime.start;
  const endTime = typeof includeTime === "boolean" ? includeTime : includeTime.end;
  // Only a value with a clock carries a zone; a day floats everywhere.
  const stamp = (token: string, timed: boolean) =>
    timed && zone && isValidTimeZone(zone) ? `${token}[${zone}]` : token;
  const from = stamp(serializeToken(start, startTime), startTime);
  if (!end || Number.isNaN(end.getTime())) return from;

  const to = stamp(serializeToken(end, endTime), endTime);
  if (to === from) return from;
  return from <= to ? `${from},${to}` : `${to},${from}`;
}

/** "9:30 am" / "09:30". */
export function formatTime(date: Date, timeFormat: TimeFormat = "12h"): string {
  return date
    .toLocaleTimeString("en-US", {
      hour: timeFormat === "24h" ? "2-digit" : "numeric",
      minute: "2-digit",
      hour12: timeFormat !== "24h",
    })
    .toLowerCase();
}

/** "Aug 12, 2026", plus the time when the value has one. */
/**
 * Zones a cell is read between. `column` is where the stored wall clock was
 * written; `viewer` is the zone to show it in. Both must be present, and the
 * cell must carry a time, for anything to move.
 */
export interface DateCellZones {
  column?: string;
  viewer?: string;
}

/** The stored clock as the viewer sees it. The value's own zone beats the column's; with neither, or no time, it stays put. */
function viewerClock(date: Date, hasTime: boolean, from: string | undefined, zones?: DateCellZones): Date {
  const source = from ?? zones?.column;
  if (!hasTime || !source || !zones?.viewer) return date;
  return convertWallClock(date, source, zones.viewer);
}

function formatOne(date: Date, hasTime: boolean, timeFormat: TimeFormat): string {
  const day = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return hasTime ? `${day} ${formatTime(date, timeFormat)}` : day;
}

/**
 * Display label for a stored cell — absolute, never relative. Database cells
 * read as dates, not as "Tomorrow"; that phrasing belongs to the @ mention.
 */
export function formatDateCellLabel(value: unknown, timeFormat: TimeFormat = "12h", zones?: DateCellZones): string {
  const cell = readDateCellInZone(value, zones);
  if (!cell) return "";
  const from = formatOne(cell.start, cell.startHasTime, timeFormat);
  if (!cell.end) return from;
  return `${from} → ${formatOne(cell.end, cell.endHasTime, timeFormat)}`;
}

/** The cell with its clocks moved into the viewer's zone — what the label shows. Editors seed from this, not parseDateCell. */
export function readDateCellInZone(value: unknown, zones?: DateCellZones): DateCell | null {
  const cell = parseDateCell(value);
  if (!cell) return null;
  const moved = cell.hasTime && !!(cell.zone ?? zones?.column) && !!zones?.viewer;
  return {
    ...cell,
    start: viewerClock(cell.start, cell.startHasTime, cell.zone, zones),
    end: cell.end ? viewerClock(cell.end, cell.endHasTime, cell.zone, zones) : null,
    zone: moved ? zones?.viewer : cell.zone,
  };
}

/** Sort key, in real time rather than in displayed digits. Empty cells sort last. */
export function dateCellSortValue(value: unknown, columnZone?: string): number | null {
  const cell = parseDateCell(value);
  if (!cell) return null;
  // A day-only cell has no instant; ordering it by its floating clock is right.
  if (!cell.hasTime) return cell.start.getTime();
  return wallClockInstant(cell.start, cell.zone ?? columnZone).getTime();
}
